// A contact / company link as the issue returns it. The API now wraps the linked record of a field with relation_fields:
//   { attribute_id, value: { uid, first_name, … }, relation_data: { role } }
// where it used to return the record itself with `relation_data` inside it:
//   { uid, first_name, …, relation_data: { role } }
// The widgets and forms read the second shape, so a wrapped link is unwrapped into it; a flat one passes through untouched.
// (Read side only: what the issue accepts back when saving is separate.)
// Imported by its own path rather than `shared/utils`, whose index pulls in the whole app.

const isObject = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v)

export const unwrapRelationItem = (item) => {
  if (!isObject(item) || !isObject(item.value)) return item
  return {
    ...item.value,
    relation_data: item.relation_data ?? item.value.relation_data,
    ...(item.attribute_id !== undefined ? { attribute_id: item.attribute_id } : {}),
  }
}

// A field's value (one link, or a list of them) with every link unwrapped
export const unwrapRelationItems = (value) => (Array.isArray(value) ? value.map(unwrapRelationItem) : unwrapRelationItem(value))

// A relation input's value as the API takes it. An option input hands back the whole option ({ name, value }, the way every form
// here builds its option lists: `{ ...o, value: o }`), but the API wants the option's own value ("Owner") and rejects the object
// ("Invalid option for relation field role"). Text, number, date and richtext values are plain already and pass through.
// An option the scheme gives no value of its own (DEAL "Contractors" -> Status: `{ name: 'Selected', value: '' }`) goes out by its
// name — `'' ?? name` would keep the empty string and the picked option would be saved as nothing.
const isBlank = (v) => v === undefined || v === null || v === ''

export const relationPayloadValue = (value) => {
  if (Array.isArray(value)) return value.map(relationPayloadValue)
  // a contact's snapshot (a contact input of the relation) is stored whole
  if (isObject(value) && (value.first_name !== undefined || value.last_name !== undefined)) return value
  if (isObject(value)) {
    // an option listed as `{ ...o, value: o }` may come back still wrapped around the scheme's own option
    if (isObject(value.value)) return relationPayloadValue(value.value)
    return isBlank(value.value) ? (isBlank(value.name) ? value : value.name) : value.value
  }
  return value
}

const isFilled = (v) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)

// A link's relation values as sent: option objects reduced to their value, then the empty ones left out
export const relationPayloadData = (relation) => Object.fromEntries(
  Object.entries(relation || {}).map(([k, v]) => [k, relationPayloadValue(v)]).filter(([, v]) => isFilled(v))
)
